import React, { useState } from 'react';
import { useOrdenCompra } from '../hooks/useOrdenesCompra';
import RecepcionOrdenCompra from './RecepcionOrdenCompra';
import HistorialRecepciones from './HistorialRecepciones';
import {
  X,
  Calendar,
  User,
  Package,
  DollarSign,
  FileText,
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  Edit,
  AlertTriangle,
} from 'lucide-react';
import type { OrdenCompra } from '../types/ordenes';
import { formatearFechaCon } from '../utils/dateUtils';

// Función para formatear moneda con separadores de miles
const formatearMoneda = (cantidad: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cantidad);
};

interface DetalleOrdenCompraProps {
  isOpen: boolean;
  onClose: () => void;
  ordenId: string;
  onEdit?: (orden: OrdenCompra) => void;
  onCambiarEstado?: (ordenId: string, nuevoEstado: string) => void;
  onNavigateToOrden?: (ordenId: string) => void;
}

const DetalleOrdenCompra: React.FC<DetalleOrdenCompraProps> = ({
  isOpen,
  onClose,
  ordenId,
  onEdit,
  onCambiarEstado,
  onNavigateToOrden,
}) => {
  const { data: orden, isLoading, error } = useOrdenCompra(ordenId, isOpen);
  const [showRecepcion, setShowRecepcion] = useState(false);

  if (!isOpen) return null;

  const estadoColor = (estado: string) => {
    switch (estado) {
      case 'borrador':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'enviada':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'recibida':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'parcial':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'cancelada':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const estadoIcon = (estado: string) => {
    switch (estado) {
      case 'borrador':
        return <Edit className="h-4 w-4" />;
      case 'enviada':
        return <Clock className="h-4 w-4" />;
      case 'recibida':
        return <CheckCircle className="h-4 w-4" />;
      case 'parcial':
        return <AlertTriangle className="h-4 w-4" />;
      case 'cancelada':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const formatearFecha = (fecha: Date | string | undefined) => {
    if (!fecha) return 'No especificada';
    return formatearFechaCon(fecha, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatearFechaHora = (fecha: Date | string | undefined) => {
    if (!fecha) return 'No especificada';
    return new Date(fecha).toLocaleString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-900 dark:text-white">
              Cargando detalles...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center space-x-3 text-red-600 mb-4">
            <AlertTriangle className="h-6 w-6" />
            <h3 className="text-lg font-semibold">Error al cargar</h3>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            No se pudieron cargar los detalles de la orden.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  if (!orden) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Orden {orden.numero}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${estadoColor(orden.estado)}`}
                >
                  {estadoIcon(orden.estado)}
                  {orden.estado
                    ? orden.estado.charAt(0).toUpperCase() +
                      orden.estado.slice(1)
                    : 'Sin estado'}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Botones de acción */}
          <div className="flex gap-2 mt-4">
            {orden.estado === 'borrador' && onEdit && (
              <button
                onClick={() => onEdit(orden)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
              >
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </button>
            )}
            {orden.estado === 'borrador' && onCambiarEstado && (
              <button
                onClick={() => onCambiarEstado(orden.id, 'enviada')}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
              >
                <Truck className="h-4 w-4 mr-2" />
                Enviar
              </button>
            )}
            {(orden.estado === 'enviada' || orden.estado === 'parcial') && (
              <button
                onClick={() => setShowRecepcion(true)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
              >
                <Package className="h-4 w-4 mr-2" />
                {orden.estado === 'parcial'
                  ? 'Recepcionar Saldo'
                  : 'Recepcionar'}
              </button>
            )}
            {(orden.estado === 'borrador' || orden.estado === 'enviada') &&
              onCambiarEstado && (
                <button
                  onClick={() => onCambiarEstado(orden.id, 'cancelada')}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancelar
                </button>
              )}
          </div>
        </div>

        {/* Contenido */}
        <div className="p-6">
          {/* Layout principal con columnas */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Columna izquierda: Progreso de recepción (si tiene recepciones parciales o está recibida con historial) */}
            {(orden.estado === 'parcial' ||
              (orden.estado === 'recibida' &&
                orden.items?.some(
                  (item) => (item.cantidadRecibida || 0) > 0
                ))) &&
              orden.items && (
                <div className="lg:col-span-1">
                  <div
                    className={`border rounded-lg p-4 ${
                      orden.estado === 'parcial'
                        ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
                        : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    }`}
                  >
                    <h3
                      className={`text-lg font-semibold mb-4 flex items-center ${
                        orden.estado === 'parcial'
                          ? 'text-orange-800 dark:text-orange-200'
                          : 'text-green-800 dark:text-green-200'
                      }`}
                    >
                      <Package className="h-5 w-5 mr-2" />
                      {orden.estado === 'parcial'
                        ? 'Progreso de Recepción'
                        : 'Historial de Recepción'}
                    </h3>

                    <div className="space-y-4">
                      {orden.items.map((item) => {
                        const cantidadPendiente =
                          item.cantidad - (item.cantidadRecibida || 0);
                        const porcentajeRecibido =
                          ((item.cantidadRecibida || 0) / item.cantidad) * 100;

                        return (
                          <div
                            key={item.id}
                            className={`rounded-lg p-3 border ${
                              orden.estado === 'parcial'
                                ? 'bg-white dark:bg-gray-800 border-orange-200 dark:border-orange-700'
                                : 'bg-white dark:bg-gray-800 border-green-200 dark:border-green-700'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                {item.ingrediente.nombre}
                              </h4>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {item.ingrediente.unidadMedida?.abreviacion ||
                                  'unidad'}
                              </span>
                            </div>

                            {/* Barra de progreso */}
                            <div className="mb-2">
                              <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                                <span>Progreso</span>
                                <span>{Math.round(porcentajeRecibido)}%</span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full transition-all duration-300 ${
                                    orden.estado === 'parcial'
                                      ? 'bg-orange-500'
                                      : 'bg-green-500'
                                  }`}
                                  style={{ width: `${porcentajeRecibido}%` }}
                                ></div>
                              </div>
                            </div>

                            {/* Cantidades */}
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div className="text-center">
                                <div className="text-gray-500 dark:text-gray-400">
                                  Solicitado
                                </div>
                                <div className="font-semibold text-gray-900 dark:text-white">
                                  {item.cantidad}
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-gray-500 dark:text-gray-400">
                                  Recibido
                                </div>
                                <div className="font-semibold text-green-600 dark:text-green-400">
                                  {item.cantidadRecibida || 0}
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-gray-500 dark:text-gray-400">
                                  Pendiente
                                </div>
                                <div
                                  className={`font-semibold ${
                                    cantidadPendiente > 0
                                      ? orden.estado === 'parcial'
                                        ? 'text-orange-600 dark:text-orange-400'
                                        : 'text-gray-600 dark:text-gray-400'
                                      : 'text-gray-400 dark:text-gray-500'
                                  }`}
                                >
                                  {cantidadPendiente}
                                </div>
                              </div>
                            </div>

                            {/* Estado del item */}
                            {cantidadPendiente > 0 && (
                              <div className="mt-2 flex items-center text-xs text-orange-600 dark:text-orange-400">
                                <Clock className="h-3 w-3 mr-1" />
                                Pendiente de recepción
                              </div>
                            )}
                            {cantidadPendiente === 0 && (
                              <div className="mt-2 flex items-center text-xs text-green-600 dark:text-green-400">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Completamente recibido
                              </div>
                            )}

                            {/* Historial de recepciones */}
                            <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                              <HistorialRecepciones
                                historial={item.historialRecepciones || []}
                                nombreIngrediente={item.ingrediente.nombre}
                                unidadMedida={
                                  item.ingrediente.unidadMedida?.abreviacion ||
                                  'unidad'
                                }
                                cantidadRecibida={item.cantidadRecibida || 0}
                                cantidadSolicitada={item.cantidad}
                              />
                            </div>
                          </div>
                        );
                      })}

                      {/* Resumen general */}
                      <div
                        className={`border-t pt-3 ${
                          orden.estado === 'parcial'
                            ? 'border-orange-200 dark:border-orange-700'
                            : 'border-green-200 dark:border-green-700'
                        }`}
                      >
                        <div
                          className={`rounded-lg p-3 ${
                            orden.estado === 'parcial'
                              ? 'bg-orange-100 dark:bg-orange-900/30'
                              : 'bg-green-100 dark:bg-green-900/30'
                          }`}
                        >
                          <h4
                            className={`text-sm font-semibold mb-2 ${
                              orden.estado === 'parcial'
                                ? 'text-orange-800 dark:text-orange-200'
                                : 'text-green-800 dark:text-green-200'
                            }`}
                          >
                            {orden.estado === 'parcial'
                              ? 'Resumen General'
                              : 'Resumen de Recepción'}
                          </h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <div
                                className={
                                  orden.estado === 'parcial'
                                    ? 'text-orange-600 dark:text-orange-400'
                                    : 'text-green-600 dark:text-green-400'
                                }
                              >
                                Items Completos
                              </div>
                              <div
                                className={`font-semibold ${
                                  orden.estado === 'parcial'
                                    ? 'text-orange-800 dark:text-orange-200'
                                    : 'text-green-800 dark:text-green-200'
                                }`}
                              >
                                {
                                  orden.items.filter(
                                    (item) =>
                                      (item.cantidadRecibida || 0) ===
                                      item.cantidad
                                  ).length
                                }{' '}
                                / {orden.items.length}
                              </div>
                            </div>
                            <div>
                              <div
                                className={
                                  orden.estado === 'parcial'
                                    ? 'text-orange-600 dark:text-orange-400'
                                    : 'text-green-600 dark:text-green-400'
                                }
                              >
                                Items Pendientes
                              </div>
                              <div
                                className={`font-semibold ${
                                  orden.estado === 'parcial'
                                    ? 'text-orange-800 dark:text-orange-200'
                                    : 'text-green-800 dark:text-green-200'
                                }`}
                              >
                                {
                                  orden.items.filter(
                                    (item) =>
                                      (item.cantidadRecibida || 0) <
                                      item.cantidad
                                  ).length
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Columna derecha: Información general */}
            <div
              className={
                orden.estado === 'parcial' ||
                (orden.estado === 'recibida' &&
                  orden.items?.some((item) => (item.cantidadRecibida || 0) > 0))
                  ? 'lg:col-span-2'
                  : 'lg:col-span-3'
              }
            >
              {/* Información general */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Información de la orden */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Información General
                  </h3>

                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Fecha de Solicitud:
                      </span>
                      <p className="text-gray-900 dark:text-white">
                        {formatearFecha(orden.fecha)}
                      </p>
                    </div>
                  </div>

                  {orden.fechaEsperada && (
                    <div className="flex items-center space-x-3">
                      <Truck className="h-5 w-5 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          Fecha Esperada:
                        </span>
                        <p className="text-gray-900 dark:text-white">
                          {formatearFecha(orden.fechaEsperada)}
                        </p>
                      </div>
                    </div>
                  )}

                  {orden.fechaRecepcion && (
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          Fecha de Recepción:
                        </span>
                        <p className="text-gray-900 dark:text-white">
                          {formatearFecha(orden.fechaRecepcion)}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Solicitado por:
                      </span>
                      <p className="text-gray-900 dark:text-white">
                        {orden.usuarioCreador?.nombre ||
                          orden.solicitante?.nombre ||
                          'No especificado'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Información del proveedor */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Proveedor
                  </h3>

                  <div className="flex items-center space-x-3">
                    <Package className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Nombre:
                      </span>
                      <p className="text-gray-900 dark:text-white">
                        {typeof orden.proveedor === 'string'
                          ? orden.proveedor
                          : orden.proveedor?.nombre}
                      </p>
                    </div>
                  </div>

                  {orden.total !== undefined && (
                    <div className="flex items-center space-x-3">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          Total:
                        </span>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatearMoneda(orden.total)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Indicador de orden generada desde recepción parcial */}
                  {orden.numeroOrdenOriginal &&
                    orden.tipo === 'saldo_pendiente' && (
                      <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                              Saldo Pendiente
                            </p>
                            <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                              Esta orden fue generada automáticamente a partir
                              de una entrega parcial de la orden{' '}
                              {onNavigateToOrden && orden.ordenOriginalId ? (
                                <button
                                  onClick={() => {
                                    onNavigateToOrden(orden.ordenOriginalId!);
                                  }}
                                  className="font-semibold underline hover:text-orange-900 dark:hover:text-orange-100 transition-colors"
                                >
                                  {orden.numeroOrdenOriginal}
                                </button>
                              ) : (
                                <span className="font-semibold">
                                  {orden.numeroOrdenOriginal}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Indicador de órdenes generadas (recepción parcial) */}
                  {orden.ordenesGeneradas &&
                    orden.ordenesGeneradas.length > 0 && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                        <div className="flex gap-3">
                          <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                              Recepción Parcial
                            </p>
                            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                              Este pedido no se recepcionó completo. Se{' '}
                              {orden.ordenesGeneradas.length === 1
                                ? 'generó la orden'
                                : 'generaron las órdenes'}
                              :{' '}
                              {orden.ordenesGeneradas.map((og, idx) => (
                                <span key={og.id}>
                                  {idx > 0 && ', '}
                                  {onNavigateToOrden ? (
                                    <button
                                      onClick={() => {
                                        onNavigateToOrden(og.id);
                                      }}
                                      className="font-semibold underline hover:text-blue-900 dark:hover:text-blue-100 transition-colors"
                                    >
                                      {og.numero}
                                    </button>
                                  ) : (
                                    <span className="font-semibold">
                                      {og.numero}
                                    </span>
                                  )}
                                </span>
                              ))}{' '}
                              para completar el saldo pendiente.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>

              {/* Observaciones */}
              {orden.observaciones && (
                <div className="mb-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <FileText className="h-5 w-5 text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Observaciones
                    </h3>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <p className="text-gray-700 dark:text-gray-300">
                      {orden.observaciones}
                    </p>
                  </div>
                </div>
              )}

              {/* Items de la orden */}
              {orden.items && orden.items.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Items de la Orden ({orden.items.length})
                  </h3>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Ingrediente
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Cantidad
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Precio Unit.
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Subtotal
                          </th>
                          {orden.estado === 'recibida' && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Recibido
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {orden.items.map((item) => (
                          <tr
                            key={item.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {item.ingrediente.nombre}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {item.ingrediente.unidadMedida?.abreviacion ||
                                    'unidad'}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {item.cantidad}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {formatearMoneda(item.precioUnitario)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {formatearMoneda(item.subtotal)}
                            </td>
                            {orden.estado === 'recibida' && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {item.cantidadRecibida || 0}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Totales */}
                  {(orden.subtotal !== undefined ||
                    orden.total !== undefined) && (
                    <div className="mt-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="space-y-2">
                        {orden.subtotal !== undefined && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">
                              Subtotal:
                            </span>
                            <span className="text-gray-900 dark:text-white">
                              {formatearMoneda(orden.subtotal)}
                            </span>
                          </div>
                        )}
                        {orden.impuestos !== undefined &&
                          orden.impuestos > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">
                                Impuestos:
                              </span>
                              <span className="text-gray-900 dark:text-white">
                                {formatearMoneda(orden.impuestos)}
                              </span>
                            </div>
                          )}
                        {orden.total !== undefined && (
                          <div className="flex justify-between text-lg font-semibold border-t border-gray-200 dark:border-gray-600 pt-2">
                            <span className="text-gray-900 dark:text-white">
                              Total:
                            </span>
                            <span className="text-gray-900 dark:text-white">
                              {formatearMoneda(orden.total)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Metadatos */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500 dark:text-gray-400">
                  <div>
                    <span className="font-medium">Creado:</span>{' '}
                    {formatearFechaHora(orden.fechaCreacion)}
                  </div>
                  <div>
                    <span className="font-medium">Actualizado:</span>{' '}
                    {formatearFechaHora(orden.fechaActualizacion)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de recepción */}
      <RecepcionOrdenCompra
        isOpen={showRecepcion}
        onClose={() => setShowRecepcion(false)}
        ordenId={ordenId}
      />
    </div>
  );
};

export default DetalleOrdenCompra;
